// Global command palette (⌘K / Ctrl+K) — "jump to anything".
//  • Pages   — every nav route the user can access (permission-filtered)
//  • People  — live employee-directory search
//  • Actions — a few core commands (new run, import, bulk salaries, theme)
// cmdk does the client-side filtering for pages/actions; people are fetched live.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, UserPlus, Upload, Moon, User as UserIcon } from 'lucide-react';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator,
} from '@/features/shared/components/ui/command';
import { useFilteredNavigation } from '@/features/navigation/hooks/useFilteredNavigation';
import { useManagementNavigation } from '@/features/navigation/hooks/useManagementNavigation';
import { useTheme } from '@/features/shared/theme/ThemeProvider';
import { useHasPermission, useHasAnyPermission } from '@/rbac/useRbac';
import { searchEmployees, type EmployeeHit } from '@/services/workforceService';
import type { UserContext } from '@/features/navigation/types';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userContext: UserContext;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onOpenChange, userContext }) => {
  const navigate = useNavigate();
  const { toggleTheme } = useTheme();
  // Same label resolution as the sidebar (navigation:<id_with_underscores>) so
  // the palette never leaks the registry's English fallback in another locale.
  const { t } = useTranslation('navigation');
  const nav = (id: string, fallback: string) => t(id.replace(/-/g, '_'), { defaultValue: fallback }) as string;
  const { navigationGroups, buildPath } = useFilteredNavigation({ userContext });
  // The manager/admin sections (Administration, Payroll, Attendance) aren't in the
  // static registry — they're RBAC-derived — so pull them from the same shared,
  // permission-gated hook the sidebar uses. This is what makes "payroll", "loan",
  // "attendance", "audit"… reachable from ⌘K (they were previously unindexed).
  const {
    adminItems, payrollItems, attendanceItems, showAdmin, showPayroll, showAttendance,
  } = useManagementNavigation();
  const [query, setQuery] = useState('');
  const [people, setPeople] = useState<EmployeeHit[]>([]);
  const [searching, setSearching] = useState(false);

  // ⌘K / Ctrl+K toggles the palette from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) { setQuery(''); setPeople([]); }
  }, [open]);

  // Live people search (debounced). Only when the user has typed something.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setPeople([]); setSearching(false); return; }
    setSearching(true);
    const h = setTimeout(() => {
      searchEmployees(q)
        .then((r) => setPeople(r.slice(0, 6)))
        .catch(() => setPeople([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(h);
  }, [query]);

  // Flatten nav (items + their children) into searchable page entries.
  const pages = useMemo(() => {
    const out: { id: string; label: string; icon?: any; path: string; group: string }[] = [];
    navigationGroups.forEach((g) => {
      const groupLabel = nav(`group-${g.id}`, g.label);
      g.items.forEach((i) => {
        out.push({ id: i.id, label: nav(i.id, i.label), icon: i.icon, path: i.path, group: groupLabel });
        (i.children || []).forEach((c: any) =>
          out.push({ id: c.id, label: nav(c.id, c.label), icon: c.icon, path: c.path, group: nav(i.id, i.label) }),
        );
      });
    });
    // Append the RBAC-derived manager/admin sections (same gates as the sidebar).
    if (showAdmin) adminItems.forEach((i) => out.push({ id: i.id, label: nav(i.id, i.label), icon: i.icon, path: i.path, group: nav('group-administration', 'Administration') }));
    if (showPayroll) payrollItems.forEach((i) => out.push({ id: i.id, label: nav(i.id, i.label), icon: i.icon, path: i.path, group: nav('group-payroll', 'Payroll') }));
    if (showAttendance) attendanceItems.forEach((i) => out.push({ id: i.id, label: nav(i.id, i.label), icon: i.icon, path: i.path, group: nav('group-attendance', 'Time & Attendance') }));
    // Dedup by id — an item defined in both the registry and a management section
    // (shouldn't happen today) would otherwise show twice.
    const seen = new Set<string>();
    return out.filter((p) => (seen.has(p.id) ? false : (seen.add(p.id), true)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationGroups, adminItems, payrollItems, attendanceItems, showAdmin, showPayroll, showAttendance, t]);

  const canRunPayroll = useHasPermission('payroll:write');
  const canImportEmployees = useHasPermission('employee:import');
  const canBulkSalaries = useHasAnyPermission(['payroll:write', 'employee:write']);

  const go = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  // Phase 4/5 follow-up (authz-hardening): staff actions are permission-
  // gated — a plain employee must not be offered "New payroll run". Same
  // any-of keys as the route guards these actions navigate into.
  const actions = [
    ...(canRunPayroll
      ? [{ id: 'a-run', label: 'New payroll run', icon: Plus, run: () => go(buildPath('payroll/runs')) }]
      : []),
    ...(canImportEmployees
      ? [{ id: 'a-import', label: 'Import employees', icon: UserPlus, run: () => go(buildPath('admin/import')) }]
      : []),
    ...(canBulkSalaries
      ? [{ id: 'a-salaries', label: 'Bulk salaries', icon: Upload, run: () => go(buildPath('payroll/salaries')) }]
      : []),
    { id: 'a-theme', label: 'Toggle dark mode', icon: Moon, run: () => { toggleTheme(); onOpenChange(false); } },
  ];

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search pages, people, actions…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>{searching ? 'Searching…' : 'No results.'}</CommandEmpty>

        <CommandGroup heading="Actions">
          {actions.map((a) => {
            const Icon = a.icon;
            return (
              <CommandItem key={a.id} value={`action ${a.label}`} onSelect={a.run}>
                <Icon className="mr-2 h-4 w-4" /> {a.label}
              </CommandItem>
            );
          })}
        </CommandGroup>

        <CommandSeparator />
        <CommandGroup heading="Pages">
          {pages.map((p) => {
            const Icon = p.icon;
            return (
              <CommandItem key={`p-${p.id}`} value={`page ${p.label} ${p.group}`} onSelect={() => go(buildPath(p.path))}>
                {Icon ? <Icon className="mr-2 h-4 w-4" /> : null}
                <span>{p.label}</span>
                <span className="ml-auto text-xs text-muted-foreground">{p.group}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>

        {people.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="People">
              {people.map((e) => (
                <CommandItem
                  key={`u-${e.id}`}
                  value={`person ${e.name} ${e.sublabel || ''}`}
                  onSelect={() => go(buildPath(`employees/${e.id}`))}
                >
                  <UserIcon className="mr-2 h-4 w-4" />
                  <span>{e.name}</span>
                  {e.sublabel && <span className="ml-2 text-xs text-muted-foreground">{e.sublabel}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
